/**
 * Fieldset
 */

import React, { useState, useRef, useEffect } from 'react';
import { useFieldValidation } from '../hooks/useFieldValidation';
import { useFormValue } from '../context/Form.context';
import { FieldsetProps } from '../types';
import { ErrorSpan } from '../Labels';
import Tooltip from 'components/Tooltip';

const Fieldset: React.FC<FieldsetProps> = (props) => {
	const {
		label,
		description,
		children,
		id,
		required,
		validationMessage,
		isInvalid,
		showError,
		visibleFields,
		className,
		helpText,
	} = props;

	const [{ invalidFields }, dispatch] = useFormValue();
	const [valid, setValid] = useState(false);
	const [touched, setTouched] = useState(false);
	const fieldRef = useRef(null);
	const [validateField, showFieldError, hideFieldError] = useFieldValidation(
		fieldRef,
		props
	);

	useEffect(() => {
		const validatedField = validateField('fieldset');

		if (validatedField.message) {
			setValid(false);
			dispatch({ type: 'FIELD_INVALID', field: validatedField });
		} else {
			setValid(true);

			if (invalidFields.includes(validatedField.id)) {
				dispatch({ type: 'FIELD_VALID', field: validatedField });
			}
		}

		const partOfVisibleFields =
			visibleFields &&
			visibleFields.filter((fieldName) => fieldName.includes(id));

		if (touched && !valid) {
			showFieldError(validatedField);
		} else if (
			(showError &&
				!valid &&
				visibleFields &&
				partOfVisibleFields &&
				partOfVisibleFields.length > 0) ||
			(showError && !valid && !visibleFields)
		) {
			setTouched(true);
			showFieldError(validatedField);
		} else {
			hideFieldError();
		}
	}, [
		valid,
		dispatch,
		touched,
		showError,
		setValid,
		validateField,
		showFieldError,
		hideFieldError,
		invalidFields,
		visibleFields,
		id,
	]);

	return (
		<fieldset
			className={className}
			aria-invalid={!valid}
			id={id}
			title={description || undefined}
			ref={fieldRef}
			onBlur={() => setTouched(true)}
		>
			<legend className="flex font-medium mb-4 text-lg leading-5">
				{label}
				{required && <abbr title="Obligatoriskt">*</abbr>}
				{helpText && (
					<Tooltip
						text={helpText}
						containerStyle="bg-yellow rounded-3xl h-6 w-6 cursor-pointer flex items-center justify-center ml-2"
						toolTipStyle="h-16 w-56 bg-grey-lighter text-gray-700 text-sm p-2"
					>
						?
					</Tooltip>
				)}
			</legend>
			<div className="py-6 border-t border-b border-grey">
				<div className="h-56 sm:h-40 flex flex-col flex-wrap">{children}</div>
			</div>

			<ErrorSpan fieldId={id} invalid={isInvalid} tabIndex={-1}>
				{validationMessage || ''}
			</ErrorSpan>
		</fieldset>
	);
};

export default Fieldset;
